import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, Upload, Link, Image } from 'lucide-react';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { MediaUploadModal } from '../../MediaUploadModal';

interface LogocloudLogo {
  name?: string;
  imageUrl?: string;
  cloudfrontUrl?: string;
  mediaId?: string;
  linkUrl?: string;
}

interface LogocloudData {
  title?: string;
  subtitle?: string;
  logos?: LogocloudLogo[];
  layout?: 'row' | 'grid';
  columns?: number;
  grayscale?: boolean;
  maxLogoHeight?: number;
  customColors?: {
    background?: string;
    title?: string;
    subtitle?: string;
  };
}

interface LogocloudEditorProps {
  formData: LogocloudData;
  setFormData: (data: LogocloudData) => void;
  siteId?: string;
}

export const LogocloudEditor: React.FC<LogocloudEditorProps> = ({
  formData,
  setFormData,
  siteId,
}) => {
  const data = formData;
  const logos = data.logos || [];
  const themeColors = useCustomColors();

  const [uploadingLogoIndex, setUploadingLogoIndex] = useState<number | null>(null);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  const updateLogo = (index: number, updates: Partial<LogocloudLogo>) => {
    const updated = [...logos];
    updated[index] = { ...updated[index], ...updates };
    setFormData({ ...data, logos: updated });
  };

  const addLogo = () => {
    setFormData({
      ...data,
      logos: [...logos, { name: '', imageUrl: '', linkUrl: '' }],
    });
  };

  const removeLogo = (index: number) => {
    setFormData({
      ...data,
      logos: logos.filter((_, i) => i !== index),
    });
  };

  const moveLogo = (from: number, to: number) => {
    if (to < 0 || to >= logos.length) return;
    const updated = [...logos];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    setFormData({ ...data, logos: updated });
  };

  const openMediaUpload = (index: number) => {
    setUploadingLogoIndex(index);
    setIsMediaModalOpen(true);
  };

  const handleMediaUploaded = (mediaUrl: string, cloudfrontUrl?: string, mediaId?: string) => {
    if (uploadingLogoIndex === null) return;
    updateLogo(uploadingLogoIndex, {
      imageUrl: cloudfrontUrl || mediaUrl,
      cloudfrontUrl,
      mediaId,
    });
    setUploadingLogoIndex(null);
    setIsMediaModalOpen(false);
  };

  const clearLogoImage = (index: number) => {
    updateLogo(index, { imageUrl: '', cloudfrontUrl: undefined, mediaId: undefined });
  };

  const colorFields = getColorFieldsForComponent('logocloud', themeColors);

  const getDisplayUrl = (logo: LogocloudLogo) => logo.cloudfrontUrl || logo.imageUrl;

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
        <input
          type="text"
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Trusted by leading companies"
          maxLength={200}
        />
      </div>

      {/* Subtitle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
        <input
          type="text"
          value={data.subtitle || ''}
          onChange={e => setFormData({ ...data, subtitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Join thousands of companies who trust us"
          maxLength={500}
        />
      </div>

      {/* Layout Options */}
      <div className="border-t pt-6">
        <h3 className="text-md font-medium text-gray-900 mb-4">Display Options</h3>

        <div className="grid grid-cols-2 gap-4">
          {/* Layout */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Layout</label>
            <select
              value={data.layout || 'row'}
              onChange={e => setFormData({ ...data, layout: e.target.value as 'row' | 'grid' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="row">Row (centered, wrapping)</option>
              <option value="grid">Grid (even columns)</option>
            </select>
          </div>

          {/* Columns - only show when layout is grid */}
          {data.layout === 'grid' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Columns (2-6)</label>
              <input
                type="number"
                min={2}
                max={6}
                value={data.columns ?? 4}
                onChange={e =>
                  setFormData({
                    ...data,
                    columns: Math.min(6, Math.max(2, parseInt(e.target.value) || 4)),
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}

          {/* Max Logo Height */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Logo Height ({data.maxLogoHeight || 48}px)
            </label>
            <input
              type="range"
              min={24}
              max={120}
              value={data.maxLogoHeight || 48}
              onChange={e => setFormData({ ...data, maxLogoHeight: parseInt(e.target.value) })}
              className="w-full"
            />
          </div>
        </div>

        {/* Grayscale Toggle */}
        <label className="flex items-center space-x-3 mt-4">
          <input
            type="checkbox"
            checked={data.grayscale !== false}
            onChange={e => setFormData({ ...data, grayscale: e.target.checked })}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-700">Grayscale logos</span>
            <p className="text-xs text-gray-500">Logos appear desaturated and colorize on hover</p>
          </div>
        </label>
      </div>

      {/* Logos List */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-md font-medium text-gray-900">Logos ({logos.length})</h3>
          <button
            type="button"
            onClick={addLogo}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
          >
            <Plus size={16} />
            <span>Add Logo</span>
          </button>
        </div>

        <div className="space-y-4">
          {logos.map((logo, index) => (
            <LogoCard
              key={index}
              logo={logo}
              index={index}
              totalLogos={logos.length}
              hasSiteId={!!siteId}
              onUpdate={updates => updateLogo(index, updates)}
              onRemove={() => removeLogo(index)}
              onMove={to => moveLogo(index, to)}
              onOpenMediaUpload={() => openMediaUpload(index)}
              onClearImage={() => clearLogoImage(index)}
              getDisplayUrl={getDisplayUrl}
            />
          ))}

          {logos.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">
                No logos added yet. Click &quot;Add Logo&quot; to get started.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Color Customization"
        description="Customize section colors. Leave empty to use theme defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
      />

      {/* Media Upload Modal */}
      <MediaUploadModal
        isOpen={isMediaModalOpen}
        onClose={() => {
          setIsMediaModalOpen(false);
          setUploadingLogoIndex(null);
        }}
        siteId={siteId || ''}
        mediaType="image"
        componentType="logocloud"
        assetType="logo"
        currentMediaUrl={
          uploadingLogoIndex !== null
            ? getDisplayUrl(logos[uploadingLogoIndex]) || undefined
            : undefined
        }
        onMediaUploaded={handleMediaUploaded}
      />
    </div>
  );
};

interface LogoCardProps {
  logo: LogocloudLogo;
  index: number;
  totalLogos: number;
  hasSiteId: boolean;
  onUpdate: (updates: Partial<LogocloudLogo>) => void;
  onRemove: () => void;
  onMove: (to: number) => void;
  onOpenMediaUpload: () => void;
  onClearImage: () => void;
  getDisplayUrl: (logo: LogocloudLogo) => string | undefined;
}

const LogoCard: React.FC<LogoCardProps> = ({
  logo,
  index,
  totalLogos: _totalLogos,
  hasSiteId,
  onUpdate,
  onRemove,
  onMove,
  onOpenMediaUpload,
  onClearImage,
  getDisplayUrl,
}) => {
  const [showUrlInput, setShowUrlInput] = useState(false);
  const displayUrl = getDisplayUrl(logo);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-start gap-3">
        {/* Move Button */}
        <div className="flex flex-col gap-1 pt-2 shrink-0">
          <button
            type="button"
            onClick={() => onMove(index - 1)}
            disabled={index === 0}
            className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            title="Move up"
          >
            <GripVertical size={14} />
          </button>
        </div>

        <div className="flex-1 min-w-0 space-y-3">
          {/* Logo Image Section */}
          <div className="bg-white border border-gray-200 rounded-md p-3">
            <label className="block text-xs font-semibold text-gray-700 mb-2">Logo Image</label>

            {displayUrl ? (
              <div className="flex items-center gap-3">
                <div className="w-20 h-12 flex items-center justify-center bg-gray-50 border border-gray-200 rounded overflow-hidden p-1">
                  <img
                    src={displayUrl}
                    alt={logo.name || 'Logo preview'}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <p className="text-xs text-gray-500 break-all line-clamp-2">{displayUrl}</p>
                </div>
                <div className="flex gap-1">
                  {hasSiteId && (
                    <button
                      type="button"
                      onClick={onOpenMediaUpload}
                      className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                      title="Change image"
                    >
                      <Upload size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onClearImage}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Remove image"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  {hasSiteId && (
                    <button
                      type="button"
                      onClick={onOpenMediaUpload}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors"
                    >
                      <Image size={14} />
                      <span>Media Library</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowUrlInput(!showUrlInput)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    <Link size={14} />
                    <span>Image URL</span>
                  </button>
                </div>

                {showUrlInput && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={logo.imageUrl || ''}
                      onChange={e =>
                        onUpdate({
                          imageUrl: e.target.value,
                          cloudfrontUrl: undefined,
                          mediaId: undefined,
                        })
                      }
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://cdn.example.com/logo.png"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Direct link to an image file (.png, .svg, .jpg)
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Name and Click-through Link */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Company Name</label>
              <input
                type="text"
                value={logo.name || ''}
                onChange={e => onUpdate({ name: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="Acme Inc."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Click-through URL
              </label>
              <input
                type="text"
                value={logo.linkUrl || ''}
                onChange={e => onUpdate({ linkUrl: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="https://acme.com"
              />
              <p className="mt-0.5 text-xs text-gray-400">
                Where users go when they click this logo
              </p>
            </div>
          </div>
        </div>

        {/* Remove company - right side, intuitively sized */}
        <button
          type="button"
          onClick={onRemove}
          className="p-2 mt-2 shrink-0 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          title="Remove company"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
};
